import { useQuery } from "@tanstack/react-query";
import { ApiService } from "../../../services/api";
import { useState } from "react";
import DataView, { QueryParameters } from "../../organisms/DataView";
import {
  PaginatedListPopulatedAPI,
  PopulatedAPI,
} from "../../../services/swagger";
import { useMatch, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useGlobalContext } from "../../../stores/Global";
import ItemCard from "../../organisms/ItemCard";
import { Chip, TableCell } from "@mui/material";
import CreateApiDialog from "../../organisms/CreateApiDialog";
import EditApiDialog from "../../organisms/EditApiDialog";

export default function ApiSettingsPage() {
  const { t } = useTranslation("ApiSettingsPage");
  const { context } = useGlobalContext();
  const navigate = useNavigate();
  const [query, setQuery] = useState<QueryParameters>();

  const match = useMatch("/m/settings/integrations/api/:_id");
  const apiId = match?.params?._id || "";
  const isCreate = apiId === "create";

  const loadApis = async (): Promise<PaginatedListPopulatedAPI | null> => {
    if (!query) return null;
    const res = await ApiService.getAPIList({
      search: query.search,
      limit: query.limit,
      skip: query.skip,
      filters: query.filters,
      sortDirection: query.sortDirection,
      sortField: query.sortField,
    });
    if (res?.data?.data) return res.data.data;
    else return null;
  };

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["api-settings", { query }],
    queryFn: loadApis,
    refetchInterval: 1000 * 10,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    placeholderData: (previousData) => previousData,
    maxPages: 10,
  });

  const navigateToApi = (api: PopulatedAPI) => {
    navigate(`/m/settings/integrations/api/${api._id}`);
  };

  const handleDataTableChange = (query: QueryParameters) => {
    setQuery(query);
  };

  const handleCloseCreateDialog = () => {
    navigate(-1);
    refetch();
  };

  const handleCloseEditDialog = () => {
    navigate(-1);
    refetch();
  };

  return (
    <>
      <CreateApiDialog open={isCreate} onClose={handleCloseCreateDialog} />
      <EditApiDialog
        _id={!isCreate && apiId ? apiId : ""}
        onClose={handleCloseEditDialog}
      />
      <DataView
        name={t("Apis")}
        loading={isLoading || !data}
        button={{
          text: t("NewApi"),
          onClick: () => navigate("/m/settings/integrations/api/create"),
        }}
        onItemClick={(item) => navigateToApi(item)}
        pagination
        count={data?.count || 0}
        items={context.user ? data?.docs || [] : []}
        headers={[
          {
            field: "description",
            name: t("Description"),
            type: "string",
          },
          {
            field: "",
            name: t("AssociatedUser"),
            type: "other",
            renderRowCell: (row: PopulatedAPI) => {
              return (
                <TableCell
                  sx={{
                    maxWidth: 200,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.user?.name}
                </TableCell>
              );
            },
          },
          {
            field: "enabled",
            name: t("Enabled"),
            type: "other",
            renderRowCell: (row: PopulatedAPI) => {
              return (
                <TableCell>
                  <Chip
                    label={row.enabled ? t("Enabled") : t("Disabled")}
                    color={row.enabled ? "success" : "default"}
                  />
                </TableCell>
              );
            },
          },
        ]}
        onChange={handleDataTableChange}
        renderItem={(api) => (
          <ItemCard api={api} onClick={() => navigateToApi(api)} />
        )}
      />
    </>
  );
}
